/**
 * "Add language" — attach one more language version to a template family.
 *
 * Posts the chosen file (plus an optional `<stem>.placeholders.json` sidecar
 * picked in the same dialog) to POST /templates/upload with `family_uuid` and
 * `language`; the backend forces the primary's owner and visibility onto the
 * new member and copies the primary's placeholder definitions when no
 * sidecar came along.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePostRequest } from '@/shared/hooks/requests.js';
import { apiErrorMessage } from '@/shared/utils/apiError.js';
import { TEMPLATE_UPLOAD } from '@/shared/utils/urls.js';
import { TEMPLATE_LANGUAGES, TEMPLATE_LANGUAGE_LABELS } from '@/shared/utils/languages.js';

const ACCEPT = '.doc,.docx,.odt,.txt,.rtf,.html,.htm,.xlsx,.json';

export function AddLanguageDialog({ family, open, onOpenChange, onAdded }) {
  const { t } = useTranslation(['my-docs']);
  const upload = usePostRequest({ url: TEMPLATE_UPLOAD, showError: false });
  const inputRef = useRef(null);
  const taken = new Set((family?.variants || []).map((v) => v.language).filter(Boolean));
  const free = TEMPLATE_LANGUAGES.filter((l) => !taken.has(l));
  const [language, setLanguage] = useState(free[0] || '');
  const [files, setFiles] = useState([]);
  // The dialog is mounted before any family is chosen: re-seed the choice
  // for the family it opens for (its first missing language).
  useEffect(() => {
    if (open) { setLanguage((free.find(Boolean)) || ''); setFiles([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, family?.uuid]);

  const templates = files.filter((f) => !f.name.toLowerCase().endsWith('.placeholders.json'));

  const submit = async () => {
    if (!family?.uuid || !language || templates.length !== 1) return;
    const formData = new FormData();
    formData.append('family_uuid', String(family.uuid));
    formData.append('language', language);
    formData.append('visibility', family.visibility || 'private');
    files.forEach((file) => formData.append('uploaded_files', file));
    const { success, errorInfo } = await upload.request({ data: formData });
    if (!success) { toast.error(apiErrorMessage(errorInfo)); return; }
    toast.success(t('my-docs:templates.languageAdded', { language: TEMPLATE_LANGUAGE_LABELS[language] || language }));
    setFiles([]);
    onOpenChange(false);
    onAdded?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('my-docs:templates.addLanguageTitle')}</DialogTitle>
          <DialogDescription>{t('my-docs:templates.addLanguageHint')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-foreground">
            {t('my-docs:templates.language')}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="mt-1 h-9 w-full rounded-[4px] border border-border bg-card px-2 text-sm text-foreground outline-none focus:border-brand"
              disabled={!free.length}
            >
              {free.map((code) => (
                <option key={code} value={code}>{TEMPLATE_LANGUAGE_LABELS[code]}</option>
              ))}
            </select>
          </label>
          {!free.length && <p className="text-xs text-muted-foreground">{t('my-docs:templates.allLanguagesPresent')}</p>}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-[4px] border border-dashed border-border bg-card px-3 text-sm text-foreground hover:bg-muted cursor-pointer"
          >
            <Upload className="h-4 w-4" />
            {templates.length ? templates[0].name : t('my-docs:templates.chooseFile')}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            data-testid="add-language-input"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > templates.length && (
            <p className="text-[11px] text-muted-foreground">{t('my-docs:templates.sidecarAttached')}</p>
          )}
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="h-8 rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground hover:bg-muted cursor-pointer">
            {t('my-docs:templates.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={upload.loading || !language || templates.length !== 1}
            className="inline-flex h-8 items-center gap-1 rounded-md bg-brand px-3 text-xs font-medium text-white disabled:opacity-50 cursor-pointer"
          >
            {upload.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {t('my-docs:templates.addLanguage')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
